import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { usePageLayout } from '@libs/common-next';
import type { ProductVariantInventory } from '@libs/algolia';

import { useProductDetails } from '@app/pdp/hooks';

import { fetchInventoryList } from '../api';

const useGetRequestParams = () => {
    const { selectedStore } = usePageLayout();
    const { variants, sddStoreId } = useProductDetails();

    const productIds = useMemo(
        () => variants.map(variant => variant.objectID),
        [variants]
    );

    const params = useMemo(
        () => ({
            productIds,
            bopis: selectedStore.store?.StoreNumber?.toString() ?? '',
            sdd: sddStoreId?.toString() ?? '',
        }),
        [productIds, sddStoreId, selectedStore.store?.StoreNumber]
    );

    return { params };
};

type Inventories = ProductVariantInventory | undefined;

/*
 * In such hooks, only data fetch and caching happens,
 * all computational processes must occur before the data is passed to the hook
 * and after it is returned. Separation will keep the hooks clean and simple.
 * Example: useGetRequestParams
 */

export const useGetInventories = () => {
    // Default inventories values (from the server) => src/providers/QueryProvider.tsx
    const queryClient = useQueryClient();
    const defaultInventoriesValues: Inventories = queryClient.getQueryData([
        'inventories-initial-data',
    ]);

    // Fetched values
    const { params } = useGetRequestParams();
    const queryKey = [
        'inventories',
        `productIds:${params.productIds.join(',')}`,
        `bopis:${params.bopis}`,
        `sdd:${params.sdd}`,
    ];

    const { data, isLoading } = useQuery({
        queryKey,
        queryFn: () => fetchInventoryList(params),
        // keep cache 24hrs: 24 * 60 * 60 * 1000
        // keep cache 1hr: 60 * 60 * 1000
        staleTime: 60 * 60 * 1000,
        // This request will not be stored in localStorage
        // meta: { persist: false },
    });

    return { inventories: data ?? defaultInventoriesValues, isLoading };
};
